import { useEffect, useRef, useState } from 'react';
import type { AuctionPhoto } from '@/types/domain';
import clsx from 'clsx';
import { haptic } from '@/lib/telegram';
import { PhotoLightbox } from '@/components/common/PhotoLightbox';
import styles from './Gallery.module.css';

interface Props {
  photos: AuctionPhoto[];
}

export const Gallery = ({ photos }: Props) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    // Определяем активное фото по скроллу
    const onScroll = () => {
      const idx = Math.round(el.scrollLeft / el.clientWidth);
      setActiveIdx(idx);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const goTo = (idx: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: idx * el.clientWidth, behavior: 'smooth' });
  };

  const openLightbox = () => {
    haptic.light();
    setLightboxOpen(true);
  };

  // Нет фото (например, проданный лот после очистки) — плейсхолдер
  if (!photos || photos.length === 0) {
    return (
      <div className={clsx(styles.wrap, styles.placeholderWrap)}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="9" cy="9" r="2" />
          <path d="m21 15-5-5L5 21" />
        </svg>
        <span className={styles.placeholderText}>Фото недоступны</span>
      </div>
    );
  }

  return (
    <>
      <div className={styles.wrap}>
        <div ref={scrollerRef} className={styles.scroller}>
          {photos.map((p) => (
            <div key={p.id} className={styles.slide}>
              <button
                type="button"
                className={styles.imageBtn}
                onClick={openLightbox}
                aria-label="Открыть фото на весь экран"
              >
                <img
                  src={p.url_original}
                  alt=""
                  className={styles.image}
                  loading="lazy"
                  decoding="async"
                />
              </button>
            </div>
          ))}
        </div>

        {/* Counter в правом верхнем */}
        {photos.length > 1 && (
          <div className={styles.counter}>
            <span className={styles.counterCurrent}>{activeIdx + 1}</span>
            <span className={styles.counterSep}>/</span>
            <span className={styles.counterTotal}>{photos.length}</span>
          </div>
        )}

        {/* Dots внизу */}
        {photos.length > 1 && (
          <div className={styles.dots}>
            {photos.map((_, i) => (
              <button
                key={i}
                className={clsx(styles.dot, i === activeIdx && styles.dotActive)}
                onClick={() => goTo(i)}
                aria-label={`Фото ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      <PhotoLightbox
        urls={photos.map((p) => p.url_original)}
        initialIndex={activeIdx}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
};